import {HttpClient} from '@angular/common/http';
import {ChangeDetectorRef, Component, inject, Inject, OnInit, PLATFORM_ID} from '@angular/core';
import {CheckoutSupportWidgetComponent} from '@app/checkout/checkout-support-widget/checkout-support-widget.component';
import {
  CmsFlexComponentData
} from '@app/checkout/order-confirmation/order-confirmation-list-item/order-confirmation-order-item.model';
import {EddlFormViewEvent} from '@app/services/analytics/EDDL/events/eddl-form-view-event';
import {CountrySelectorService} from '@app/services/country-selector.service';
import {TransactionTypeService} from '@app/services/transactionTypes/transaction-type.service';
import {DcpSupportFormService} from '@app/shared/support-form/dcp-support-form.service';
import {
  ConfigService,
  DcpGlobalMessageService,
  FeatureTogglesService,
  JsonService,
  TransactionType
} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {EventService, PageMetaService, Product} from '@spartacus/core';
import {CmsComponentData, CurrentProductService} from '@spartacus/storefront';
import {distinctUntilChanged, take} from 'rxjs/operators';
import {DcpProductUtilitiesService} from '../../services/dcp-product-utilities.service';
import {PARTNER_SLM_TRANSACTION_TYPE} from '../publisher/publisher.component';
import {
  ActionTypeSelectStrategyService
} from '@app/services/action-type-select-strategy/action-type-select-strategy.service';
import {UNLEASH_TOGGLES} from '@app/config/FeatureToggleConfig';
import {LuigiContainer} from '@luigi-project/container';
import {FormDataTrackingService} from '@app/services/analytics/form-data-tracking.service';
import {LeadFormLuigiContext} from '@app/product/lead/lead.model';

const leadFormActionType: string = 'popup';

@UntilDestroy()
@Component({
  selector: 'dcp-product-support-widget',
  templateUrl: './product-support-widget.component.html',
  styleUrls: ['./product-support-widget.component.scss']
})
export class ProductSupportWidgetComponent extends CheckoutSupportWidgetComponent implements OnInit {

  isAgentAvailable: boolean = true;
  isSapProduct: boolean;
  isProductDetailsPage: boolean;
  public product: Product;
  public partnerTransactionType: TransactionType;
  isSupportWidgetOpen: boolean = false;
  public useWebComponentLeadForm: boolean = false;
  isSearchPage: boolean = false;
  sapChatAdvisorEnabled: boolean = false

  public modalId: string = 'modal-lead-form';
  public container: LuigiContainer;

  private pageMetaService: PageMetaService = inject(PageMetaService);

  constructor(
    @Inject(PLATFORM_ID) platformId: string,
    public cdr: ChangeDetectorRef,
    cmsData: CmsComponentData<CmsFlexComponentData>,
    jsonService: JsonService,
    countrySelectorService: CountrySelectorService,
    dcpSupportFormService: DcpSupportFormService,
    private currentProductService: CurrentProductService,
    public dcpProductUtilitiesService: DcpProductUtilitiesService,
    private transactionTypeService: TransactionTypeService,
    private actionTypeSelectStrategyService: ActionTypeSelectStrategyService,
    private http: HttpClient,
    private dcpGlobalMessageService: DcpGlobalMessageService,
    private configService: ConfigService,
    private eventService: EventService,
    private togglesService: FeatureTogglesService,
    public formDataTrackingService: FormDataTrackingService,
  ) {
    super(platformId, cmsData, jsonService, countrySelectorService, dcpSupportFormService);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.togglesService.isEnabled(UNLEASH_TOGGLES.useLeadFormWebComoponent)
      .pipe(untilDestroyed(this))
      .subscribe((isEnabled) => {
        this.useWebComponentLeadForm = isEnabled;
      });

    this.transactionTypeService.getTransactionTypes$().pipe(untilDestroyed(this)).subscribe((transactionTypes: TransactionType[]) => {
      this.partnerTransactionType = transactionTypes.find(transactionType => transactionType.code === PARTNER_SLM_TRANSACTION_TYPE.code);
    });
    this.currentProductService.getProduct().pipe(untilDestroyed(this)).subscribe((product: Product) => {
      this.isProductDetailsPage = !!product;
      if (product) {
        this.product = product;
        this.isSapProduct = this.dcpProductUtilitiesService.isSapProduct(this.product);
      }
    });
    this.http.get<{[key: string]: boolean}>(this.configService.getString('chatAgentAvailable.baseUrl'), {
      params: this.configService.getObject('chatAgentAvailable.params'),
    }).pipe(untilDestroyed(this), take(1)).subscribe(
      (data) => {
        this.isAgentAvailable = this.agentAvailable(data);
        this.cdr.detectChanges();
      },
      (error) => {
        this.dcpGlobalMessageService.addMessage(error, this?.constructor?.name, 'errorLogging');
      }
    );

    this.checkCurrentPage();

    this.togglesService
      .isEnabled(UNLEASH_TOGGLES.sapChatAdvisor)
      .pipe(untilDestroyed(this))
      .subscribe((isEnabled) => {
        this.sapChatAdvisorEnabled = isEnabled;
      });
  }

  openLeadRfiForm(): void {
    const actionTypeContext = this.actionTypeSelectStrategyService.selectStrategy(
      this.partnerTransactionType,
      this.product,
      PARTNER_SLM_TRANSACTION_TYPE,
      undefined,
    );
    this.triggerEddlFormViewEvent();
    actionTypeContext.exec();
  }

  openEddlModal() {
    const cont: LuigiContainer = document.querySelector('#dcp-lead-form-modal');
    const context: LeadFormLuigiContext = cont?.context as unknown as LeadFormLuigiContext;
    if (context?.modalId) {
      this.modalId = context?.modalId
    }

    window.dispatchEvent(
      new CustomEvent(`dcp-lead-form:modal:open:${this.modalId}`)
    )

  }

  private triggerEddlFormViewEvent(): void {
    if (this.partnerTransactionType.actionType === leadFormActionType) {
      const event = new EddlFormViewEvent();
      event.name = PARTNER_SLM_TRANSACTION_TYPE.name;
      this.eventService.dispatch(event);
    }
  }

  private agentAvailable(statuses: {[key: string]: boolean}): boolean {
    return !!statuses && Object.values(statuses).some(Boolean);
  }

  toggleSupportWidget() {
    this.isSupportWidgetOpen = !this.isSupportWidgetOpen;
    this.cdr.detectChanges();
  }

  /**
   * On search page, we don't want to show the support widget.
   * SAP Product Advisor will be shown instead.
   */
  checkCurrentPage(): void {
    this.pageMetaService.getMeta()
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged()
      )
      .subscribe((meta) => {
        let urlSegments = meta?.canonicalUrl?.split('/') || [];
        urlSegments.forEach(segment => {
          if (segment === 'search') {
            this.isSearchPage = true;
          }
        })
      })
  }

  showSupportWidget(): boolean {
    return !(this.isSearchPage && this.sapChatAdvisorEnabled);
  }
}
